import React, { useState, useEffect, useMemo, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Grid,
  TextField,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import { usePayPalScriptReducer } from '@paypal/react-paypal-js';
import PayPalCheckoutButton from '../components/PayPalCheckoutButton';
import ErrorBoundary from '../components/ErrorBoundary';
import { doc, getDoc, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase/firebase';

// PayPal错误边界处理组件
class ErrorBoundaryPayPal extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      retryTime: 0,
      isInitialRender: true, // 标记初始渲染
      initialDelay: true,    // 初始延迟显示标记
    };
    
    // 初始不显示PayPal按钮，先显示加载中，避免立即出错
    this.renderTimeoutId = null;
  }

  componentDidMount() {
    // 延迟显示PayPal按钮，给系统一些时间加载资源
    this.renderTimeoutId = setTimeout(() => {
      this.setState({ 
        initialDelay: false, 
        isInitialRender: false,
      });
    }, 1000); // 1秒延迟
  }
  
  componentWillUnmount() {
    // 清理定时器
    if (this.renderTimeoutId) {
      clearTimeout(this.renderTimeoutId);
    }
  }

  static getDerivedStateFromError(error) {
    // 更新状态，下一次渲染将显示回退 UI
    return { 
      hasError: true, 
      error: error,
      isInitialRender: false,
      initialDelay: false
    };
  }

  componentDidCatch(error, errorInfo) {
    // 记录错误信息
    console.error('PayPal按钮错误:', error, errorInfo);
    this.setState({ errorInfo: errorInfo });
  }

  // 点击重试按钮时触发
  handleRetry = () => {
    const retryCount = this.state.retryCount + 1;
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: retryCount,
      retryTime: Date.now(),
      initialDelay: true
    });
    
    // 再次延迟显示按钮
    this.renderTimeoutId = setTimeout(() => {
      this.setState({ initialDelay: false });
    }, 500);
  }

  // 点击刷新整个页面
  handleRefresh = () => {
    window.location.reload();
  }

  render() {
    // 如果初始加载状态，显示加载指示器
    if (this.state.initialDelay) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={30} sx={{ mr: 2 }} />
          <Typography variant="body2">
            正在准备PayPal支付...
          </Typography>
        </Box>
      );
    }
    
    // 如果发生错误，显示回退UI
    if (this.state.hasError) {
      // 检查是否是PayPal相关错误
      const isPayPalError = this.state.error?.message?.includes('paypal') || 
                           this.state.error?.message?.includes('PayPal') ||
                           this.state.error?.message?.includes('Buttons');

      // 判断错误类型
      let errorMessage = "加载支付组件时出错";
      let errorDetails = "支付系统暂时不可用，请稍后再试";
      
      if (isPayPalError) {
        if (this.state.error?.message?.includes('undefined')) {
          errorMessage = "PayPal组件未能完全加载";
          errorDetails = "浏览器可能未能加载所有必要的PayPal资源";
        } else {
          errorMessage = "PayPal支付组件加载失败";
        }
      }

      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2">{errorMessage}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>{errorDetails}</Typography>
          </Alert>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button 
              variant="contained"
              color="primary" 
              onClick={this.handleRetry}
              disabled={this.state.retryCount >= 3}
              startIcon={<span role="img" aria-label="重试">🔄</span>}
            >
              {this.state.retryCount < 3 ? "重新加载支付组件" : "重试次数过多"}
            </Button>
            
            <Button 
              variant="outlined"
              onClick={this.handleRefresh}
              startIcon={<span role="img" aria-label="刷新">🔃</span>}
            >
              刷新页面
            </Button>
          </Box>
          
          {this.state.retryCount >= 2 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              如果问题持续存在，您可以尝试清除浏览器缓存后重试
            </Typography>
          )}
        </Box>
      );
    }

    // 正常渲染子组件
    try {
      const { items, shippingCost, shippingInfo, addressSaved } = this.props;
      
      // 增加错误捕获层
      return (
        <React.Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>加载支付按钮...</Typography>
          </Box>
        }>
          <PayPalCheckoutButton 
            key={`paypal-checkout-${this.state.retryTime || Date.now()}`}
            shippingCost={shippingCost} 
            shippingAddress={addressSaved ? {
              name: shippingInfo.fullName,
              phone: shippingInfo.phone,
              address: {
                address_line_1: shippingInfo.streetAddress + (shippingInfo.buildingName ? `, ${shippingInfo.buildingName}` : ''),
                admin_area_2: shippingInfo.region,
                admin_area_1: shippingInfo.country,
                postal_code: shippingInfo.postalCode,
                country_code: shippingInfo.country === 'Singapore' ? 'SG' : 
                            shippingInfo.country === '中国' ? 'CN' : 
                            shippingInfo.country === 'China' ? 'CN' : 'SG'
              }
            } : null}
          />
        </React.Suspense>
      );
    } catch (error) {
      console.error('渲染PayPal按钮错误:', error);
      // 如果即使是在try/catch中仍然出错，显示紧急回退UI
      return (
        <Box sx={{ p: 3, textAlign: 'center', border: '1px solid #f0f0f0', borderRadius: 1 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            支付组件加载失败，请刷新页面重试
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
          >
            刷新页面
          </Button>
        </Box>
      );
    }
  }
}

// 样式化组件
const CartItemPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  }
}));

const ItemImage = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius / 2
}));

const QuantityControl = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1)
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5)
}));

const OrderSummary = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  position: 'sticky',
  top: theme.spacing(2)
}));

// 结账步骤 - 简化为直接结账
const steps = ['查看购物车', 'PayPal支付并填写配送信息'];

const IntegratedCheckout = () => {
  const { items, totalAmount, updateQuantity, removeItem, clearCart, addItem } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [{ isPending }] = usePayPalScriptReducer();

  const [loading, setLoading] = useState(true); // 设置初始状态为loading=true
  const [error, setError] = useState('');
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [pageReady, setPageReady] = useState(false); // 添加一个页面就绪状态
  
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    phone: '',
    country: 'Singapore',
    region: '',
    streetAddress: '',
    buildingName: '',
    postalCode: ''
  });
  
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressSaved, setAddressSaved] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  
  const [imageUrls, setImageUrls] = useState({});
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  
  const hasItems = items.length > 0;
  
  // 计算运费
  const calculateShippingCost = () => {
    if (!hasItems) return 0;
    const thresholds = items
      .filter(item => item.freeShippingThreshold > 0)
      .map(item => item.freeShippingThreshold);
    
    const minThreshold = thresholds.length > 0 ? Math.min(...thresholds) : 100;
    
    if (totalAmount >= minThreshold) {
      return 0;
    }
    
    const productShippingFees = items
      .filter(item => item.shippingFee > 0)
      .reduce((total, item) => total + (item.shippingFee * item.quantity), 0);
    
    return productShippingFees > 0 ? productShippingFees : 10;
  };

  const shippingCost = calculateShippingCost();
  const orderTotal = totalAmount + shippingCost;

  // 格式化价格
  const formatPrice = (price) => {
    return `S$${price.toFixed(2)}`;
  };

  // 改进的页面就绪状态处理
  useEffect(() => {
    // 立即设置页面为可见状态，不再等待500ms延迟
    setPageReady(true);
    
    // 使用requestAnimationFrame确保在下一次渲染周期结束加载状态
    // 这给了浏览器足够的时间来渲染初始UI
    requestAnimationFrame(() => {
      setLoading(false);
    });
  }, []);

  // 加载用户地址
  useEffect(() => {
    const loadUserAddresses = async () => {
      if (!currentUser) {
        setLoadingAddresses(false); // 确保未登录用户也取消加载状态
        return;
      }
      
      setLoadingAddresses(true);
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let userAddresses = [];
          let defaultAddressFound = false;
          
          // 首先尝试加载新格式的shipping字段
          if (userData.shipping && userData.shipping.address) {
            console.log('找到新格式的shipping地址');
            const newFormatAddr = {
              id: 'shipping_new_format',
              name: userData.shipping.name || '',
              phone: userData.shipping.phone || '',
              province: userData.shipping.address.admin_area_1 || 'Singapore',
              city: userData.shipping.address.admin_area_2 || '',
              address: userData.shipping.address.address_line_1 || '',
              postalCode: userData.shipping.address.postal_code || '',
              isDefault: true
            };
            
            userAddresses.push(newFormatAddr);
            setSelectedAddressId('shipping_new_format');
            setShippingInfo({
              fullName: newFormatAddr.name,
              phone: newFormatAddr.phone,
              country: newFormatAddr.province,
              region: newFormatAddr.city,
              streetAddress: newFormatAddr.address,
              buildingName: '',
              postalCode: newFormatAddr.postalCode
            });
            defaultAddressFound = true;
            setAddressSaved(true);
          }
          
          // 然后加载地址数组中的其他地址（但不重复设置为默认地址）
          if (userData.addresses && userData.addresses.length > 0) {
            console.log('加载地址列表，总数:', userData.addresses.length);
            // 确保不添加重复的shippingAddress
            const filteredAddresses = userData.addresses.filter(addr => {
              // 如果已经找到了默认地址并且这个地址也标记为默认，则不要添加它
              if (defaultAddressFound && addr.isDefault) {
                return false;
              }
              return true;
            });
            
            userAddresses = [...userAddresses, ...filteredAddresses];
            
            // 只有当没有找到默认地址时，才从地址列表中选一个作为默认
            if (!defaultAddressFound) {
              const defaultAddress = userData.addresses.find(addr => addr.isDefault);
              if (defaultAddress) {
                setSelectedAddressId(defaultAddress.id);
                setShippingInfo({
                  fullName: defaultAddress.name || '',
                  phone: defaultAddress.phone || '',
                  country: defaultAddress.province || 'Singapore',
                  region: defaultAddress.city || '',
                  streetAddress: defaultAddress.address || '',
                  buildingName: '',
                  postalCode: defaultAddress.postalCode || ''
                });
                setAddressSaved(true);
                defaultAddressFound = true;
              }
            }
          }
          
          // 如果还没有找到默认地址，尝试使用旧格式的shippingAddress
          if (!defaultAddressFound && userData.shippingAddress) {
            console.log('使用旧格式的shippingAddress');
            const shippingAddr = {
              id: 'shipping_default',
              name: userData.shippingAddress.fullName || '',
              phone: userData.shippingAddress.phone || '',
              province: userData.shippingAddress.country || 'Singapore',
              city: userData.shippingAddress.region || '',
              address: userData.shippingAddress.streetAddress + 
                (userData.shippingAddress.buildingName ? 
                  `, ${userData.shippingAddress.buildingName}` : '') || '',
              postalCode: userData.shippingAddress.postalCode || '',
              isDefault: true
            };
            
            // 避免重复添加
            if (!userAddresses.some(addr => addr.id === 'shipping_default')) {
              userAddresses.push(shippingAddr);
              setSelectedAddressId('shipping_default');
              setShippingInfo({
                fullName: shippingAddr.name,
                phone: shippingAddr.phone,
                country: shippingAddr.province,
                region: shippingAddr.city,
                streetAddress: shippingAddr.address,
                buildingName: '',
                postalCode: shippingAddr.postalCode
              });
              setAddressSaved(true);
            }
          }
          
          console.log('最终加载的地址数量:', userAddresses.length);
          setAddresses(userAddresses);
        }
      } catch (error) {
        console.error('Error loading addresses:', error);
        setError('Failed to load addresses / 加载地址失败');
      } finally {
        setLoadingAddresses(false);
      }
    };

    loadUserAddresses();
  }, [currentUser]);

  // 处理地址选择
  const handleAddressSelect = (addressId) => {
    const selectedAddress = addresses.find(addr => addr.id === addressId);
    if (selectedAddress) {
      setSelectedAddressId(addressId);
      setShippingInfo({
        fullName: selectedAddress.name,
        phone: selectedAddress.phone,
        country: selectedAddress.province,
        region: selectedAddress.city,
        streetAddress: selectedAddress.address,
        buildingName: '',
        postalCode: selectedAddress.postalCode
      });
      setAddressSaved(true);
    }
  };

  // 处理地址表单变化
  const handleShippingInfoChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({
      ...prev,
      [name]: value
    }));
    setAddressSaved(false);
  };

  // 保存地址到 Firestore
  const handleSaveAddress = async () => {
    if (!currentUser) {
      setError('Please login first / 请先登录');
      return;
    }

    // 验证必填字段
    const requiredFields = ['fullName', 'phone', 'country', 'region', 'streetAddress', 'postalCode'];
    const missingFields = requiredFields.filter(field => !shippingInfo[field]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in all required fields / 请填写所有必填字段`);
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // 创建新地址对象 (旧格式，用于兼容性)
      const newAddress = {
        id: `addr_${Date.now()}`,
        name: shippingInfo.fullName,
        phone: shippingInfo.phone,
        province: shippingInfo.country,
        city: shippingInfo.region,
        district: '',
        address: shippingInfo.streetAddress + (shippingInfo.buildingName ? `, ${shippingInfo.buildingName}` : ''),
        postalCode: shippingInfo.postalCode,
        isDefault: true,
        createdAt: new Date().toISOString()
      };

      // 获取当前用户数据
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        let updatedAddresses = userData.addresses || [];
        
        // 将其他地址的默认状态设为false
        updatedAddresses = updatedAddresses.map(addr => ({
          ...addr,
          isDefault: false
        }));
        
        // 添加新地址
        updatedAddresses = [newAddress, ...updatedAddresses];
        
        // 国家代码转换
        const countryCode = shippingInfo.country === 'Singapore' ? 'SG' : 
                           shippingInfo.country === '中国' ? 'CN' : 
                           shippingInfo.country === 'China' ? 'CN' : 
                           shippingInfo.country === 'Malaysia' ? 'MY' : 
                           shippingInfo.country === '马来西亚' ? 'MY' : 'SG';
        
        // 更新 Firestore - 同时保存两种格式的地址
        await updateDoc(userRef, {
          // 1. 地址列表 (旧格式)
          addresses: updatedAddresses,
          
          // 2. 旧格式的shippingAddress字段
          shippingAddress: {
            fullName: shippingInfo.fullName,
            phone: shippingInfo.phone,
            country: shippingInfo.country,
            region: shippingInfo.region,
            streetAddress: shippingInfo.streetAddress,
            buildingName: shippingInfo.buildingName,
            postalCode: shippingInfo.postalCode,
            updatedAt: new Date().toISOString()
          },
          
          // 3. 新格式的shipping字段 (PayPal格式)
          shipping: {
            name: shippingInfo.fullName,
            phone: shippingInfo.phone,
            address: {
              address_line_1: shippingInfo.streetAddress + (shippingInfo.buildingName ? `, ${shippingInfo.buildingName}` : ''),
              admin_area_2: shippingInfo.region,
              admin_area_1: shippingInfo.country,
              postal_code: shippingInfo.postalCode,
              country_code: countryCode
            },
            updatedAt: new Date().toISOString()
          }
        });

        // 4. 尝试更新shipping子集合中的default文档，但捕获权限错误
        try {
          const shippingRef = doc(db, 'users', currentUser.uid, 'shipping', 'default');
          await setDoc(shippingRef, {
            fullName: shippingInfo.fullName,
            phone: shippingInfo.phone,
            address_line_1: shippingInfo.streetAddress + (shippingInfo.buildingName ? `, ${shippingInfo.buildingName}` : ''),
            admin_area_2: shippingInfo.region,
            admin_area_1: shippingInfo.country,
            postal_code: shippingInfo.postalCode,
            country_code: countryCode,
            updatedAt: new Date().toISOString()
          });
          console.log('shipping子集合更新成功');
        } catch (subCollectionError) {
          // 权限错误不影响主要功能，只记录日志
          console.warn('shipping子集合更新失败 (权限问题):', subCollectionError);
          // 不返回错误给用户，因为主要信息已经保存
        }

        // 更新本地状态
        setAddresses(updatedAddresses);
        setSelectedAddressId(newAddress.id);
        setAddressSaved(true);
        
        // 保存成功后隐藏表单
        setShowNewAddressForm(false);
        
        // 显示成功消息
        setError('');
        setSnackbarMessage('Address saved successfully / 地址保存成功');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving address:', error);
      setError('Failed to save address / 保存地址失败');
    } finally {
      setLoading(false);
    }
  };

  // 添加 Snackbar 状态
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // 处理 Snackbar 关闭
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // 重写图片加载逻辑，使其更可靠
  useEffect(() => {
    // 检查商品是否存在
    if (!items || items.length === 0) {
      return;
    }

    // 立即为所有商品设置默认图片，不等待任何异步操作
    const defaultImageUrls = {};
    const initialLoadingStates = {};
    
    // 先为所有项目设置默认图片和加载状态
    items.forEach(item => {
      if (item && item.id) {
        // 使用项目当前的图片URL或默认图片
        defaultImageUrls[item.id] = imageUrls[item.id] || '/placeholder.png';
        // 设置初始加载状态
        initialLoadingStates[item.id] = !imageUrls[item.id]; // 如果已有URL，则不需要加载
      }
    });
    
    // 立即更新状态，确保组件有内容渲染，无阻塞
    setImageUrls(prev => ({ ...prev, ...defaultImageUrls }));
    setImageLoadingStates(prev => ({ ...prev, ...initialLoadingStates }));
    
    // 使用单个异步函数处理所有图片加载，而不是每个图片一个单独的异步操作
    const loadImagesAsync = async () => {
      try {
        console.log("开始加载购物车商品图片，商品数：", items.length);
        const storage = getStorage();
        
        // 只获取需要加载的项目
        const itemsToLoad = items.filter(item => 
          item && 
          item.id && 
          item.imageUrl && 
          (!imageUrls[item.id] || imageUrls[item.id] === '/placeholder.png')
        );
        
        if (itemsToLoad.length === 0) {
          console.log("所有图片已加载，无需加载更多");
          return;
        }
        
        console.log("需要加载图片的商品数：", itemsToLoad.length);
        
        // 异步加载所有图片，使用Promise.allSettled确保一个失败不会影响其他图片
        const loadPromises = itemsToLoad.map(async (item) => {
          try {
            // 默认为占位符
            let imageUrl = '/placeholder.png';
            
            // 如果已经是完整URL，直接使用
            if (item.imageUrl.startsWith('http')) {
              imageUrl = item.imageUrl;
              console.log(`商品 ${item.id} 使用HTTP图片URL`);
            } else {
              try {
                // 从Firebase Storage获取
                const imageRef = ref(storage, item.imageUrl);
                imageUrl = await getDownloadURL(imageRef);
                console.log(`商品 ${item.id} 从Storage加载图片成功`);
              } catch (storageError) {
                console.error(`无法从Storage加载商品 ${item.id} 的图片:`, storageError);
                imageUrl = '/placeholder.png';
                throw storageError; // 传递错误
              }
            }
            
            // 返回结果对象
            return { 
              id: item.id, 
              url: imageUrl, 
              success: true 
            };
          } catch (error) {
            // 出错时返回错误结果
            return { 
              id: item.id, 
              url: '/placeholder.png', 
              success: false, 
              error: error.message || '加载图片失败' 
            };
          }
        });
        
        // 等待所有图片加载完成，无论成功或失败
        const results = await Promise.allSettled(loadPromises);
        
        // 处理结果，批量更新状态
        const newUrls = {};
        const newErrors = {};
        const newLoadingStates = {};
        
        results.forEach(result => {
          if (result.status === 'fulfilled') {
            const data = result.value;
            newUrls[data.id] = data.url;
            newErrors[data.id] = data.success ? null : data.error;
            newLoadingStates[data.id] = false;
          } else if (result.status === 'rejected') {
            // 这种情况应该很少，因为我们在每个promise中都捕获了错误
            const itemId = itemsToLoad[results.indexOf(result)].id;
            newUrls[itemId] = '/placeholder.png';
            newErrors[itemId] = '加载失败';
            newLoadingStates[itemId] = false;
          }
        });
        
        // 批量更新状态，减少重渲染次数
        setImageUrls(prev => ({ ...prev, ...newUrls }));
        setImageErrors(prev => ({ ...prev, ...newErrors }));
        setImageLoadingStates(prev => ({ ...prev, ...newLoadingStates }));
        
        console.log("图片加载完成，成功:", 
          Object.values(newErrors).filter(err => err === null).length,
          "失败:", 
          Object.values(newErrors).filter(err => err !== null).length
        );
      } catch (error) {
        // 捕获整体错误，确保不会导致组件崩溃
        console.error("加载购物车商品图片时发生错误:", error);
      }
    };

    // 使用requestAnimationFrame确保DOM更新后再加载图片
    // 这样用户可以立即看到界面，然后图片会在后台加载
    requestAnimationFrame(() => {
      loadImagesAsync();
    });
    
  }, [items]); // 仅依赖items，减少不必要的重新运行

  // 渲染购物车商品
  const renderCartItems = () => {
    if (!hasItems) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            Your cart is empty / 购物车是空的
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/shop')}
            sx={{ mt: 2 }}
          >
            Continue Shopping / 继续购物
          </Button>
        </Box>
      );
    }

    // 显示错误保护
    try {
      return (
        <List>
          {items.map((item) => {
            // 跳过无效项目
            if (!item || !item.id) {
              console.error("发现无效购物车项目:", item);
              return null;
            }
            
            return (
              <ListItem
                key={item.id}
                sx={{
                  mb: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1
                }}
              >
            <ListItemAvatar>
              <Avatar
                variant="rounded"
                sx={{ width: 80, height: 80 }}
              >
                {imageLoadingStates[item.id] ? (
                  <CircularProgress size={40} />
                ) : (
                  <Box
                    component="img"
                    src={imageUrls[item.id] || '/placeholder.png'}
                    alt={item.name || '商品图片'}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      console.error(`Image load error for item ${item.id}:`, e);
                      // 防止无限循环错误
                      if (e.target.src !== '/placeholder.png') {
                        e.target.src = '/placeholder.png';
                      }
                      setImageErrors(prev => ({
                        ...prev,
                        [item.id]: '加载图片失败'
                      }));
                    }}
                  />
                )}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="subtitle1">
                  {item.name}
                  {imageErrors[item.id] && (
                    <Typography variant="caption" color="error" component="span" sx={{ ml: 1 }}>
                      {imageErrors[item.id]}
                    </Typography>
                  )}
                </Typography>
              }
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {formatPrice(item.price)}
                </Typography>
              }
              sx={{ ml: 2 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <IconButton
                size="small"
                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                disabled={item.quantity <= 1}
              >
                <RemoveIcon />
              </IconButton>
              <Typography sx={{ mx: 2 }}>{item.quantity}</Typography>
              <IconButton
                size="small"
                onClick={() => updateQuantity(item.id, item.quantity + 1)}
              >
                <AddIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => removeItem(item.id)}
                sx={{ ml: 2 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </ListItem>
            );
          })}
        </List>
      );
    } catch (renderError) {
      console.error("渲染购物车商品出错:", renderError);
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="error" gutterBottom>
            显示购物车内容时出错
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              clearCart(); // 清空购物车，修复损坏的数据
              setSnackbarOpen(true);
              setSnackbarMessage('购物车数据已重置，请重新添加商品');
            }}
            sx={{ mt: 2 }}
          >
            重置购物车
          </Button>
        </Box>
      );
    }
  };

  // 渲染地址列表
  const renderAddressList = () => {
    if (loadingAddresses) {
      return (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      );
    }

    if (addresses.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" my={2}>
          No saved addresses / 没有保存的地址
        </Typography>
      );
    }

    // 将默认地址放在最前面显示
    const sortedAddresses = [...addresses].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return 0;
    });
    
    // 只显示3个地址，避免页面过长
    const visibleAddresses = sortedAddresses.slice(0, 3);
    const hasMoreAddresses = sortedAddresses.length > 3;

    return (
      <>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">
            {addresses.length > 1 
              ? "Select shipping address / 选择收货地址" 
              : "Default shipping address / 默认收货地址"}
          </FormLabel>
          <RadioGroup
            value={selectedAddressId || ''}
            onChange={(e) => handleAddressSelect(e.target.value)}
          >
            {visibleAddresses.map((address) => (
              <FormControlLabel
                key={address.id}
                value={address.id}
                control={<Radio />}
                label={
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: address.isDefault ? 'rgba(25, 118, 210, 0.08)' : 'transparent' }}>
                    <Typography variant="subtitle1">
                      {address.name} {address.phone && `- ${address.phone}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {address.address}
                      {address.city && `, ${address.city}`}
                      {address.province && `, ${address.province}`}
                      {address.postalCode && ` ${address.postalCode}`}
                    </Typography>
                    {address.isDefault && (
                      <Typography variant="caption" color="primary">
                        Default Address / 默认地址
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
        
        {hasMoreAddresses && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            And {sortedAddresses.length - 3} more addresses... / 还有 {sortedAddresses.length - 3} 个地址...
          </Typography>
        )}
        
        <Divider sx={{ my: 2 }} />
      </>
    );
  };

  // 更新加载状态的处理方式，确保内容始终可见
  const isLoading = loading; // 简化逻辑
  const isEmptyCart = !hasItems;
  
  // 在实际开始渲染之前，先确保我们有数据可以显示
  useEffect(() => {
    if (hasItems && !isLoading && !isPending) {
      // 如果有商品且不再加载中，确保页面就绪状态为true
      setPageReady(true);
    }
  }, [hasItems, isLoading, isPending]);

  // 渲染结账页面
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* 加载指示器 - 修改为更小的顶部通知，而不是全屏遮挡 */}
      {isLoading && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 3,
          p: 2, 
          borderRadius: 1,
          bgcolor: 'rgba(237, 247, 237, 0.8)', 
          border: '1px solid #81c784'
        }}>
          <CircularProgress size={24} sx={{ mr: 2 }} color="success" />
          <Typography variant="body2">
            正在加载购物车信息，您可以继续浏览...
          </Typography>
        </Box>
      )}
      
      {/* 主要购物车内容 - 始终显示，不再使用透明度遮挡 */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {/* 购物车商品列表 */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Shopping Cart / 购物车
            </Typography>
            {isEmptyCart ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
                  购物车中没有商品
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/shop')}
                >
                  浏览商品
                </Button>
              </Box>
            ) : (
              renderCartItems()
            )}
          </Paper>

          {/* 收货地址表单 */}
          {hasItems && (
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Shipping Address / 收货地址
              </Typography>
              
              {renderAddressList()}
              
              {/* "新增地址"按钮和表单 */}
              <Box sx={{ mt: 3, mb: 2 }}>
                {/* 添加一个状态变量来控制新地址表单的显示 */}
                {!showNewAddressForm ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowNewAddressForm(true)}
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                  >
                    Add New Address / 添加新地址
                  </Button>
                ) : (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      New Address / 新增地址
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Full Name / 收货人"
                          name="fullName"
                          value={shippingInfo.fullName}
                          onChange={handleShippingInfoChange}
                          required
                          error={!shippingInfo.fullName && !addressSaved}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone / 电话"
                          name="phone"
                          value={shippingInfo.phone}
                          onChange={handleShippingInfoChange}
                          required
                          error={!shippingInfo.phone && !addressSaved}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Country / 国家"
                          name="country"
                          value={shippingInfo.country}
                          onChange={handleShippingInfoChange}
                          required
                          error={!shippingInfo.country && !addressSaved}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Region/City / 地区/城市"
                          name="region"
                          value={shippingInfo.region}
                          onChange={handleShippingInfoChange}
                          required
                          error={!shippingInfo.region && !addressSaved}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Street Address / 街道地址"
                          name="streetAddress"
                          value={shippingInfo.streetAddress}
                          onChange={handleShippingInfoChange}
                          required
                          error={!shippingInfo.streetAddress && !addressSaved}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Building Name / 建筑名称(可选)"
                          name="buildingName"
                          value={shippingInfo.buildingName}
                          onChange={handleShippingInfoChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Postal Code / 邮政编码"
                          name="postalCode"
                          value={shippingInfo.postalCode}
                          onChange={handleShippingInfoChange}
                          required
                          error={!shippingInfo.postalCode && !addressSaved}
                        />
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveAddress}
                        disabled={loading}
                      >
                        {loading ? 'Saving...' : 'Save Address / 保存地址'}
                      </Button>
                      
                      <Button
                        variant="outlined"
                        onClick={() => setShowNewAddressForm(false)}
                      >
                        Cancel / 取消
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          )}

          {/* PayPal 按钮 - 直接支付 */}
          {hasItems && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Payment / 支付
              </Typography>
              
              {addressSaved ? (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    收货地址已保存，您可以直接使用PayPal完成支付。
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    建议先保存收货地址，然后再使用PayPal支付。
                  </Typography>
                </Alert>
              )}

              {/* 使用DirectPayPalButton组件作为主要支付选项 */}
              <Box sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
                {React.createElement(() => {
                  // 使用动态导入DirectPayPalButton组件
                  // 这样即使组件导入失败也不会阻止整个页面渲染
                  const DirectPayPalButtonWrapper = () => {
                    const [ButtonComponent, setButtonComponent] = useState(null);
                    const [error, setError] = useState(false);
                    
                    useEffect(() => {
                      // 尝试动态导入
                      try {
                        const DirectPayPalButton = require('../components/DirectPayPalButton').default;
                        setButtonComponent(() => DirectPayPalButton);
                      } catch (err) {
                        console.error('加载DirectPayPalButton失败:', err);
                        setError(true);
                      }
                    }, []);
                    
                    if (error) {
                      // 回退到简单按钮
                      return (
                        <>
                          <Button 
                            variant="contained" 
                            color="primary"
                            fullWidth
                            size="large"
                            onClick={() => {
                              window.location.href = `/checkout-server?amount=${orderTotal}&items=${encodeURIComponent(JSON.stringify(items))}`; 
                            }}
                            sx={{ mb: 2, height: 50, bgcolor: '#0070ba', '&:hover': { bgcolor: '#005ea6' } }}
                          >
                            使用PayPal支付 ({formatPrice(orderTotal)})
                          </Button>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                            点击按钮后将跳转到PayPal支付页面
                          </Typography>
                        </>
                      );
                    }
                    
                    if (!ButtonComponent) {
                      // 加载中状态
                      return (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ ml: 2 }}>加载支付组件...</Typography>
                        </Box>
                      );
                    }
                    
                    // 成功导入，渲染DirectPayPalButton
                    return (
                      <>
                        <ButtonComponent 
                          shippingCost={shippingCost}
                          customStyle={{ mb: 2 }}
                        />
                      </>
                    );
                  };
                  
                  return <DirectPayPalButtonWrapper />;
                })}
              </Box>
              
              {/* 提供备用支付选项 */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  如果支付过程中遇到问题，您可以：
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => window.location.reload()}
                  sx={{ mr: 2 }}
                >
                  刷新页面
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary"
                  onClick={() => {
                    // 强制重置Cart页面状态
                    localStorage.setItem('cart-reset', Date.now());
                    window.location.href = '/cart?reload=true';
                  }}
                >
                  重置购物车
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                {addressSaved 
                  ? '您的收货地址将自动传递给PayPal，无需再次填写。' 
                  : '如果未保存地址，您需要在PayPal页面填写收货信息。'}
              </Typography>
            </Paper>
          )}
        </Grid>

        {/* 订单摘要 */}
        <Grid item xs={12} md={4}>
          <OrderSummary>
            <Typography variant="h5" gutterBottom>
              Order Summary / 订单摘要
            </Typography>
            <Box sx={{ my: 2 }}>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Typography>Subtotal / 小计</Typography>
                </Grid>
                <Grid item>
                  <Typography>{formatPrice(totalAmount)}</Typography>
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between" sx={{ mt: 1 }}>
                <Grid item>
                  <Typography>Shipping / 运费</Typography>
                </Grid>
                <Grid item>
                  <Typography>{formatPrice(shippingCost)}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Typography variant="h6">Total / 总计</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="h6">{formatPrice(orderTotal)}</Typography>
                </Grid>
              </Grid>
            </Box>
          </OrderSummary>
        </Grid>
      </Grid>

      {/* 错误提示 */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* 添加 Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default IntegratedCheckout;